import React, { useRef } from 'react';
import { GlobalStyles, Paper, styled } from '@mui/material';
import GooglePlacesAutocomplete from 'react-google-autocomplete';

interface LocationSearchInputProps {
  handlesearchLocation: (data: {
    latLng: { lat: number; lng: number };
    city: string;
    state: string;
    country: string;
    formattedAddress?: string;
  }) => void;
  address?: string;
  setAddress?: React.Dispatch<React.SetStateAction<string>>;
  disabled?: boolean;
}

const InputWrapper = styled(Paper)(() => ({
  border: '1px solid #ddd',
  borderRadius: '4px',
  boxShadow: 'none',
  width: '400px',
  height: '42px',
}));

const LocationSearchInput: React.FC<LocationSearchInputProps> = ({
  handlesearchLocation,
  address,
  setAddress,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSelect = (place: google.maps.places.PlaceResult) => {
    if (!place.geometry?.location) return;

    const latLng = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };

    const addressComponents = place.address_components || [];

    const getComponent = (type: string) =>
      addressComponents.find((c) => c.types.includes(type))?.long_name || '';

    const city =
      getComponent('locality') ||
      getComponent('sublocality') ||
      getComponent('administrative_area_level_2');

    const state = getComponent('administrative_area_level_1');
    const country = getComponent('country');
    const formattedAddress = place.formatted_address || place.name || '';

    setAddress?.(formattedAddress);
    handlesearchLocation({ latLng, city, state, country, formattedAddress });
  };

  return (
    <>
      <GlobalStyles
        styles={{
          '.pac-container': {
            zIndex: 2000,
            width: '800px',
          },
        }}
      />
      <InputWrapper>
        <GooglePlacesAutocomplete
          apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
          onPlaceSelected={handleSelect}
          options={{
            types: ['geocode'],
          }}
          value={address}
          onChange={(e) => {
            const value = (e.target as HTMLInputElement).value;
            // Remove special characters except letters, numbers, spaces, commas, and basic punctuation
            const sanitizedValue = value.replace(/[^a-zA-Z0-9\s,.-]/g, '');
            
            // Remove leading spaces (before first letter) but keep spaces after first letter
            const noLeadingSpaces = sanitizedValue.replace(/^\s+/, '');
            
            // Prevent multiple consecutive spaces
            const noMultipleSpaces = noLeadingSpaces.replace(/\s+/g, ' ');
            
            setAddress?.(noMultipleSpaces);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault(); // ✅ Prevent form submit
            }
          }}
          placeholder="Location"
          inputRef={inputRef}
          style={{
            width: '100%',
            border: 'none',
            outline: 'none',
            fontSize: '15px',
            padding: '10px 8px',
            fontFamily: 'Livvic',
            backgroundColor: 'transparent',
          }}
          disabled={disabled}
        />
      </InputWrapper>
    </>
  );
};

export default LocationSearchInput;
